<template>
    <div :class="['tournament-position-card', { 'rank-1': rank === 1 }]">
         <img
            v-if="showMedalImages && medalSrc"
            :src="medalSrc"
            class="medal-img"
            :class="medalSizeClass"
         />
        <p class="place-txt"><b>{{ getPlaceText(rank) }}</b></p>
        <p class='name-txt should-blur'>
            <template v-if="showBrokerAccount && brokerAccountId">
                {{ brokerAccountId }}
            </template>
            <template v-else-if="!showBrokerAccount && showIbNumber && ibNumber">
                {{ ibNumber }}
            </template>
            <template v-else>
                {{ name.substring(0,11) + ( name.length > 10 ? '...' : '' )}}
            </template>
            <Flag :code="countryCode" v-if="countryCode" />
        </p>
    </div>
</template>

<script>
export default {
    name: 'TournamentPositionCard',
    props: {
        name: {
            type: String,
            required: true
        },
        rank: {
            type: Number,
            required: true,
            default: 1
        },
        countryCode: {
            type: String,
            required: false,
            default: null
        },
        brokerAccountId: {
            type: String,
            required: false,
            default: null
        },
        showBrokerAccount: {
            type: Boolean,
            default: false
        },
        showIbNumber: {
            type: Boolean,
            default: false
        },
        ibNumber: {
            type: [String, Number],
            default: null
        },
        medalType: {
            type: String,
            default: "default"
        },
        showMedalImages: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        medalClass() {
            switch (this.rank) {
                case 1:
                    return 'gold';
                case 2:
                    return 'silver';
                case 3:
                    return 'bronze';
                default:
                    return '';
            }
        },

        normalizedMedalType() {
             const t = (this.medalType || "default").toString().toLowerCase();
             const allowed = ["default", "3d", "neon", "retro"];
             return allowed.includes(t) ? t : "default";
        },

        medalSrc() {
            if (![1, 2, 3].includes(this.rank)) return null;

            const folder = this.normalizedMedalType || "default";

            try {
                return require(`@/assets/icons/${folder}/Medal-${this.rank}.png`);
            } catch (e) {
                try {
                    return require(`@/assets/icons/default/Medal-${this.rank}.png`);
                    } catch (e2) {
                    return null;
                }   
            }
        },
        
        medalSizeClass(){
            if (this.rank === 1) return "gold";
            if (this.rank === 2) return "silver";
            if (this.rank === 3) return "bronze";
            return "";
        },
    },
    methods: {
        getOrdinalRank(rank) {
            const suffixes = this.$i18n.locale === 'es' ? ['º', 'º', 'º', 'º'] : ['th', 'st', 'nd', 'rd'];
            const v = rank % 100;
            return rank + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
        },
        getPlaceText(rank) {
            // For Thai, the translation includes the rank number in the format "อันดับที่ {rank}"
            if (this.$i18n.locale === 'th') {
                return this.$t("place_TournamentPositionCard", { rank: rank });
            }
            // For Vietnamese: Giải Nhất (1st), Giải Nhì (2nd), Giải Ba (3rd), then "Hạng {rank}"
            if (this.$i18n.locale === 'vi') {
                if (rank === 1) return this.$t("place_TournamentPositionCard_1");
                if (rank === 2) return this.$t("place_TournamentPositionCard_2");
                if (rank === 3) return this.$t("place_TournamentPositionCard_3");
                return this.$t("place_TournamentPositionCard_rank", { rank });
            }
            // For other languages, use the ordinal rank + translation
            return `${this.getOrdinalRank(rank)} ${this.$t("place_TournamentPositionCard")}`;
        },
    },
}
</script>

<style lang="scss" scoped>
.tournament-position-card {
    display: flex;
    flex-direction: column;
    border: 1px solid #9A9A9A;
    border-radius: 15px;
    background-color: var(--v-fullWhite-base); /* Hardcoded white color */
    height: fit-content;
    font-family: "Avenir", sans-serif;
    padding-top: 0.25px;
    flex-grow: 1;
    
    &.rank-1 {
        margin: 0 20px; /* Añade margin a los lados solo cuando el rank es 1 */
    }

    .medal {
        margin: 0 auto;
        &.gold {
            font-size: 64px;
            color: #FFD700; /* Hardcoded gold color */
            padding: 0 36px;
        }
        &.silver {
            font-size: 48px;
            color: #C0C0C0; /* Hardcoded silver color */
            padding: 0 24px;
        }
        &.bronze {
            font-size: 32px;
            color: #CD7F32; /* Hardcoded bronze color */
            padding: 0 12px;
        }
        &.md {
            font-size: 2rem;
        }
        &.lg {
            font-size: 3rem;
        }
        &.sm {
            font-size: 1rem;
        }
    }

    .place-txt {
        text-align: center;
        font-size: 24px;
        font-weight: 700;
        padding: 20px;
        padding-bottom: 15px;
    }

    .name-txt {
        text-align: center;
        font-size: 16px;
        display: flex;
        padding-bottom: 10px;
        align-items: center;
        justify-content: center;
        .flag {
            margin-left: 0.5rem;
        }
        
        &.broker-account {
            font-family: monospace;
            font-size: 14px;
            background-color: rgba(0, 0, 0, 0.05);
            border-radius: 4px;
            padding: 2px 6px;
            margin: 0 10px;
        }
    }
}

/* Mobile styles */
@media screen and (max-width: 768px) {
    .tournament-position-card {
        min-height: 160px;
        width: 100%; /* Changed from 90% to 100% to fill carousel item */
        margin: 0 !important; /* Remove margins for carousel layout */
        
        &.rank-1 {
            min-height: 170px;
            margin: 0 !important; /* Override rank-1 margins for mobile */
        }
        
        .medal {
            &.gold {
                font-size: 72px;
                padding: 0 24px;
            }
            &.silver {
                font-size: 68px;
                padding: 0 24px;
            }
            &.bronze {
                font-size: 62px;
                padding: 0 24px;
            }
        }
        
        .place-txt {
            font-size: 28px !important;
            padding: 10px;
            margin-bottom: 10px;
            padding-bottom: 0;
        }
        
        .name-txt {
            font-size: 14px;
        }
    }
}

.medal-img {
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

/* tamaños similares a tu icono */
.medal-img.gold {
  width: 64px;
  height: 64px;
  margin-top: 6px;
}
.medal-img.silver {
  width: 48px;
  height: 48px;
  margin-top: 10px;
}
.medal-img.bronze {
  width: 32px;
  height: 32px;
  margin-top: 14px;
}

/* Mobile overrides (similar a los que ya tienes) */
@media screen and (max-width: 768px) {
  .medal-img.gold { width: 72px; height: 72px; }
  .medal-img.silver { width: 68px; height: 68px; }
  .medal-img.bronze { width: 62px; height: 62px; }
}


</style>
